/* eslint-disable import/prefer-default-export */
import { getTipoDePadronPorContenType } from 'api/catalogos';
import Moment, { isMoment } from 'moment';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { retrievePadronesMap } from 'api/recaudacion/utils';
import padronToString from './padronesToString';

const DATE_FORMAT = 'YYYYMMDD - HHmm';

export const TIPOS_DE_DOCUMENTO_ENUM = Object.freeze({
  RECIBO: 'Recibo',
  DOCUMENTO: 'Documento',
  ORDEN_DE_PAGO: 'Orden de pago',
});

/**
 * Función que recibe un objeto con la información de un documento y regresa el nombre del archivo
 * @param {Object} data - Objeto con la información del documento
 * @param {String} data.folio - Folio del documento
 * @param {String} data.id - ID del documento
 * @param {String} data.fecha_de_creacion - Fecha de creación del documento
 * @param {Object} data.padron - Objeto con la información del padrón
 * @param {Number} data.padron.tipo_padron - Tipo de padrón
 * @param {Number} data.content_type_padron - Content type del padrón
 * @param {String} tipoDeDocumento - Tipo de documento
 * @returns {String} Nombre del archivo
 * @example
 * getFileName({
 *  data: {
 *  folio: '123',
 *  fecha_de_creacion: '2021-01-01T00:00:00',
 *  tipoDeDocumento: 'Recibo',
 *  padron: {
 *    tipo_padron: 1,
 *    content_type_padron: 123,
 *  },
 * });
 */
export const getFileName = ({ data, tipoDeDocumento }) => {
  if (!data) return TIPOS_DE_DOCUMENTO_ENUM.DOCUMENTO;

  const {
    content_type_padron,
    fecha_de_creacion,
    folio,
    id,
    padron,
  } = data;

  const tipoPadron = padron?.tipo_padron
    || padron?.tipo_de_padron
    || getTipoDePadronPorContenType(content_type_padron);

  const tipoDocumento = tipoDeDocumento || TIPOS_DE_DOCUMENTO_ENUM.DOCUMENTO;

  const _folio = folio || id;

  const currentDate = new Moment();
  let fecha = isMoment(fecha_de_creacion)
    ? fecha_de_creacion.format(DATE_FORMAT)
    : new Moment(fecha_de_creacion).format(DATE_FORMAT);

  if (!fecha) fecha = currentDate.format(DATE_FORMAT);

  const padronString = padronToString(padron, tipoPadron, true).split(' - ')[0];

  let output = `${padronString} - ${tipoDocumento} ${_folio} - ${fecha}`
    .replace(/:/g, '')
    .replace(/\//g, '')
    .trim();

  if (output.startsWith('- ')) {
    output = output.substring(2);
  }

  return output.toUpperCase();
};

/**
 * Genera el nombre de un archivo basado en los datos del trámite y su padrón asociado.
 *
 * @async
 * @param {Object} tramite - Datos del trámite.
 * @param {string} tramite.content_type_a_padron - Tipo de contenido asociado al padrón.
 * @param {string} tramite.object_id_padron - ID del padrón asociado al trámite.
 * @param {string} tramite.contribuyente - ID del contribuyente en caso de no haber un padrón
 * explícito.
 * @param {string} tramite.descripcion - Descripción del trámite utilizada en el nombre del archivo.
 *
 * @returns {Promise<string>} Nombre generado para el archivo.
 */
export const buildFileName = async (tramite) => {
  const tipoPadron = getTipoDePadronPorContenType(tramite?.content_type_a_padron)
    ?? padronesEnum.CONTRIBUYENTE;

  const padronId = tramite?.object_id_padron || tramite?.contribuyente;
  const padron = await retrievePadronesMap?.[tipoPadron]?.(padronId);

  return getFileName({
    data: {
      ...tramite,
      padron: { ...padron, tipo_padron: tipoPadron },
    },
    tipoDeDocumento: tramite.descripcion,
  });
};

/**
 * Verifica si un trámite tiene los campos necesarios para permitir la descarga.
 *
 * @param {Object} tramite - Objeto que representa el trámite.
 * @param {string} [tramite.content_type_a_padron] - Tipo de contenido asociado al padrón.
 * @param {string} [tramite.object_id_padron] - ID del padrón asociado al trámite.
 * @param {string} [tramite.folio] - Folio del trámite.
 * @param {string} [tramite.id] - Identificador único del trámite.
 * @param {string} [tramite.descripcion] - Descripción del trámite.
 * @param {string} [tramite.contribuyente] - ID del contribuyente en caso de no haber un padrón
 * explícito.
 *
 * @returns {boolean} `true` si el trámite tiene los campos necesarios para la descarga,
 * de lo contrario `false`.
 */
export const checkIfCanDownload = (tramite) => {
  const fieldSets = [
    ['content_type_a_padron', 'object_id_padron', 'folio', 'id', 'descripcion'],
    ['contribuyente', 'folio', 'id', 'descripcion'],
  ];

  return fieldSets.some((fields) => fields.every((field) => tramite?.[field]));
};
